/**
 * theme-toggle.js — Designer Heat Miami V2
 *
 * Handles light/dark theme switching:
 * - Reads saved preference from localStorage ('dh-theme')
 * - Falls back to prefers-color-scheme media query
 * - Applies data-theme attribute on <html>
 * - Dispatches 'theme-changed' custom event
 * - Wires up to toggle button(s) with id="theme-toggle"
 *
 * Note: FOUC prevention is handled by an inline script in theme.liquid
 * that runs before this file loads.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'dh-theme';
  var LIGHT = 'light';
  var DARK = 'dark';

  /**
   * Get the current theme from the DOM attribute.
   * @returns {string} 'light' or 'dark'
   */
  function getCurrentTheme() {
    return document.documentElement.getAttribute('data-theme') || LIGHT;
  }

  /**
   * Apply a theme: set attribute, persist to localStorage, update ARIA,
   * and dispatch a custom event.
   * @param {string} theme - 'light' or 'dark'
   */
  function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      /* localStorage may be blocked — fail silently */
    }

    /* Update aria-label on all toggle buttons */
    var buttons = document.querySelectorAll('[data-theme-toggle]');
    var label = theme === DARK
      ? 'Switch to light mode'
      : 'Switch to dark mode';

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-label', label);
    }

    /* Dispatch event so other components can react */
    document.dispatchEvent(new CustomEvent('theme-changed', {
      bubbles: true,
      detail: { theme: theme }
    }));
  }

  /**
   * Toggle between light and dark.
   */
  function toggle() {
    var current = getCurrentTheme();
    applyTheme(current === DARK ? LIGHT : DARK);
  }

  /**
   * Listen for system preference changes (only applies if user hasn't
   * manually set a preference via the toggle).
   */
  function listenForSystemChange() {
    if (!window.matchMedia) return;

    var mq = window.matchMedia('(prefers-color-scheme: dark)');

    function handleChange(e) {
      /* Only follow system if user hasn't explicitly toggled */
      var saved;
      try {
        saved = localStorage.getItem(STORAGE_KEY);
      } catch (err) {
        saved = null;
      }

      if (!saved) {
        applyTheme(e.matches ? DARK : LIGHT);
      }
    }

    /* Modern browsers */
    if (mq.addEventListener) {
      mq.addEventListener('change', handleChange);
    } else if (mq.addListener) {
      /* Safari < 14 */
      mq.addListener(handleChange);
    }
  }

  /**
   * Wire up toggle buttons after DOM is ready.
   */
  function initButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-theme-toggle]');
      if (btn) {
        e.preventDefault();
        toggle();
      }
    });
  }

  /* Initialize */
  listenForSystemChange();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initButtons);
  } else {
    initButtons();
  }

  /* Expose for Alpine.js or other scripts */
  window.theme = window.theme || {};
  window.theme.toggleTheme = toggle;
  window.theme.applyTheme = applyTheme;
  window.theme.getCurrentTheme = getCurrentTheme;
})();
